import React from "react";
import {FaClock, FaUser, FaMapMarkerAlt, FaCommentDots, FaStore} from "react-icons/fa";
// Self-hosted (bundled into the build, no runtime fetch to fonts.googleapis.com —
// external domains have been unreliable from this network all session, e.g. the
// NHTSA car-data API) — matches the weights used in OrderCard.css.
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/dm-sans/800.css";
import "./OrderCard.css";

function pad(n) {
  return `${n}`.padStart(2, '0');
}

function formatTime(timestampSeconds) {
  if (!timestampSeconds) {
    return '-';
  }
  const date = new Date(timestampSeconds * 1000);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Turda admins also see Câmpia Turzii's restaurants mixed into the same
// dashboard (see the backend's ListAllOrders, which pulls both cities' orders
// once the deliverer's own city is Turda) — this tag is the only thing that
// tells them apart at a glance.
function restaurantDisplayName(order) {
  return order.restaurant_city === 'Câmpia Turzii'
    ? `${order.restaurant_name} C. Turzii`
    : order.restaurant_name;
}

// created/accepted share the "in preparation" look — accepted doesn't have
// its own card yet, created is the placeholder for it until it does.
function statusVariant(order, nowSeconds) {
  if (order.status === 'prepared') {
    return 'prepared';
  }
  if (order.status === 'picked') {
    return order.user_eta && nowSeconds > order.user_eta ? 'overdue' : 'picked';
  }
  return 'preparing';
}

const VARIANT_LABELS = {
  preparing: 'In Preparare',
  prepared: 'Preparată',
  picked: 'Ridicată',
  overdue: 'Ridicată',
};

export default function OrderCard({order, now, duplicateName, onClick, dragHandleProps, style, innerRef}) {
  const nowSeconds = Math.floor((now || Date.now()) / 1000);
  const variant = statusVariant(order, nowSeconds);
  const overdue = variant === 'overdue';
  // Not accepted by the restaurant yet — the eta-pill shows this instead of
  // any countdown, since there's nothing to count down to until it's picked
  // up by the restaurant.
  const unconfirmed = order.status === 'created';

  // restaurant_eta is unset (not yet quoted by the restaurant) as a large
  // negative unix timestamp — Go's zero time.Time value.
  const restaurantMinutesLeft = order.restaurant_eta > 0
    ? Math.round((order.restaurant_eta - nowSeconds) / 60)
    : null;
  // Once picked up, the badge counts up from picked_at instead of counting
  // down to restaurant_eta — the kitchen ETA is no longer the relevant clock.
  const minutesSincePicked = order.status === 'picked' && order.picked_at
    ? Math.max(0, Math.floor((nowSeconds - order.picked_at) / 60))
    : null;

  // How far along the Plasată→Timp estimativ line "now" sits, as a percent —
  // null when there's no estimate to measure against (e.g. pickup orders).
  const progressPercent = (order.created && order.user_eta && order.user_eta > order.created)
    ? Math.min(100, Math.max(0, ((nowSeconds - order.created) / (order.user_eta - order.created)) * 100))
    : null;

  return (
    <div
      ref={innerRef}
      {...dragHandleProps}
      style={style}
      onClick={onClick}
      className={`order-card order-card--${variant}`}
    >
      <div className="order-card-header">
        <div className="order-card-header-left">
          <span className="order-card-id" title={VARIANT_LABELS[variant]}>#{order.order_id}</span>
          <div className="order-card-customer-row">
            <span className="order-card-avatar order-card-avatar--sm"><FaUser size={11}/></span>
            <span className="order-card-customer-name">{order.user_name}</span>
          </div>
        </div>
        {unconfirmed ? (
          <div className="order-card-eta-pill order-card-eta-pill--unconfirmed">
            <span>Neconfirmată</span>
          </div>
        ) : minutesSincePicked !== null ? (
          <div className="order-card-eta-pill">
            {/*<FaClock size={22}/>*/}
            <span>{minutesSincePicked} min</span>
          </div>
        ) : restaurantMinutesLeft !== null && (
          <div className="order-card-eta-pill">
            {/*<FaClock size={22}/>*/}
            <span>{restaurantMinutesLeft > 0 ? `${restaurantMinutesLeft} min` : 'Gata'}</span>
          </div>
        )}
      </div>

      <div className="order-card-timeline">
        <div className="order-card-timeline-track-wrap">
          <div className="order-card-timeline-track">
            <span className="order-card-timeline-dot"/>
            <div className="order-card-timeline-line">
              <div className="order-card-timeline-line-fill" style={{width: `${progressPercent || 0}%`}}/>
              {progressPercent !== null && (
                <span className="order-card-timeline-now-dot" style={{left: `${progressPercent}%`}}/>
              )}
            </div>
            <span className="order-card-timeline-dot"/>
          </div>
        </div>
        <div className="order-card-timeline-labels">
          <div className="order-card-timeline-item">
            <span className="order-card-timeline-value">{formatTime(order.created)}</span>
          </div>
          {overdue && <span className="order-card-overdue-badge">Timp Depășit!</span>}
          <div className="order-card-timeline-item order-card-timeline-item--right">
            <span className="order-card-timeline-value">{formatTime(order.user_eta)}</span>
          </div>
        </div>
      </div>

      <div className={`order-card-info${duplicateName ? ' order-card-info--alert' : ''}`}>
        <div className="order-card-row">
          <span className="order-card-restaurant-icon">
            {order.restaurant_logo ? <img src={order.restaurant_logo} alt="" className="order-card-restaurant-logo"/> : <FaStore size={18}/>}
          </span>
          <span className="order-card-restaurant-name">{restaurantDisplayName(order)}</span>
        </div>

        <div className="order-card-divider"/>

        <div className="order-card-row">
          <span className="order-card-avatar"><FaMapMarkerAlt size={18}/></span>
          <div className="order-card-address-block">
            <span className="order-card-address">{order.address}</span>
            {order.mentions && (
              <span className="order-card-mentions-badge">
                <FaCommentDots size={14}/>
                {order.mentions}
              </span>
            )}
          </div>
        </div>
      </div>

      {order.voucher !== 0 && <div className="order-card-voucher">{order.voucher} lei voucher</div>}
    </div>
  );
}
